import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import trash from "../../../../../assets/images/trash.svg";
import eye from "../../../../../assets/images/eye.svg";
import downloadIcon from "../../../../../assets/images/downloadIcon.svg";
import dayjs from "dayjs";
import ConfirmDeletePopup from "../../../../../components/ConfirmDeletePopup";
import Loader from "../../../../../components/Loader";
import VerifiedPopup from "../../../../../components/VerifiedPopup";
import { useVerifiedStore } from "../../../../../store/useVerifiedStore";
import { useDriverStore } from "../../../../../store/useDriverStore";
import {
  deleteDocument,
  getDocument,
  getDriverDetails,
} from "../../../../../services/dashboard";

interface DocumentRow {
  id: number;
  documentName: string;
  documentType: string;
  uploadedBy: string;
  date: string;
  file: File;
}

export default function DriverDocuments() {
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [popupLabel, setPopupLabel] = useState("");
  const [openPreview, setOpenPreview] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<any>(null);

  const { selectedDriver, setSelectedDriver } = useDriverStore();
  const { showVerified, setShowVerified } = useVerifiedStore();

  const documentsList = selectedDriver?.documents || [];
  const driverNumber = selectedDriver?.driverNumber || "";

  //   const handleAdd = async () => {
  //     if (!selectedFile || !documentType || !uniqueId) {
  //       showInfoToast("Please select file and document type");
  //       return;
  //     }

  //     try {
  //       setIsLoading(true);

  //       const formData = new FormData();

  //       formData.append("caseId", uniqueId);
  //       formData.append(documentType, selectedFile);

  //       const response = await uploadDocuments(formData);

  //       if (response?.status === 200) {
  //        await refreshCase();
  //         setPopupLabel("Uploaded");
  //         setShowVerified(true);

  //         setSelectedFile(null);
  //         setDocumentType("");
  //         if (fileInputRef.current) fileInputRef.current.value = "";
  //       }
  //     } catch (error) {
  //       console.error("Upload failed", error);
  //     } finally {
  //       setIsLoading(false);
  //     }
  //   };

  const handlePreview = async (documentId: any) => {
    const response = await getDocument(driverNumber, documentId);
    if (response.status === 200) {
      const blob = response.data;
      const url = URL.createObjectURL(blob);
      setSelectedDoc({ file_url: url });
      setOpenPreview(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedDocId || !driverNumber) return;

    try {
      setIsLoading(true);
      const response = await deleteDocument(driverNumber, selectedDocId);

      if (response.status === 200) {
        await refreshDriver();
        setPopupLabel("Deleted");
        setShowVerified(true);
        setShowConfirmDelete(false);
        setSelectedDocId(null);
      }
    } catch (error) {
      console.error("Delete failed", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async (documentId: any, fileName: any) => {
    try {
      const res = await getDocument(driverNumber, documentId);
      if (!res || res.status !== 200) {
        throw new Error("Failed to download document");
      }

      const blob = res.data;
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = fileName || "document.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading file:", err);
    }
  };

  const handleCloseConfirmPopup = () => {
    setShowConfirmDelete(false);
  };
  const handleClosePreview = () => {
    setOpenPreview(false);
    setSelectedDoc(null);
  };

  const refreshDriver = async () => {
    if (!selectedDriver?.driverNumber) return;

    const res = await getDriverDetails(selectedDriver.driverID);
    if (res.status === 200) {
      setSelectedDriver(res.data);
    }
  };

  useEffect(() => {
    if (showVerified) {
      const timer = setTimeout(() => {
        setShowVerified(false);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [showVerified]);

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      {/* Table Wrapper */}
      {isLoading && <Loader isLoading={isLoading} />}
      {documentsList.length > 0 ? (
        <Box sx={{ border: "1px solid #D8DDDA", overflow: "hidden" }}>
          {/* Responsive pass (F.9): this table had no scroll boundary at all,
              and sits inside two overflow:hidden ancestors — so on a handheld
              the trailing Action column (preview / delete / download) was
              clipped with no way to scroll to it. */}
          <TableContainer sx={{ overflowX: "auto" }}>
          <Table sx={{ minWidth: 620 }}>
            {/* Table Header */}
            <TableHead sx={{ backgroundColor: "background.paper" }}>
              <TableRow>
                <TableCell sx={headerCellStyle}>Document Name</TableCell>
                <TableCell sx={headerCellStyle}>Document Type</TableCell>
                <TableCell sx={headerCellStyle}>Uploaded By</TableCell>
                <TableCell sx={headerCellStyle}>Date</TableCell>
                <TableCell sx={headerCellStyle}>Action</TableCell>
              </TableRow>
            </TableHead>

            {/* Table Body */}
            <TableBody>
              {documentsList?.map((row: any, index: any) => (
                <TableRow key={index}>
                  <TableCell sx={bodyCellStyle}>{row.file_name}</TableCell>
                  <TableCell sx={{ ...bodyCellStyle, color: "#6b6b6b" }}>
                    {row.document_type}
                  </TableCell>
                  <TableCell sx={bodyCellStyle}>{row.created_by}</TableCell>
                  <TableCell sx={bodyCellStyle}>
                    {dayjs(row.created_on).format("YYYY-MM-DD")}
                  </TableCell>
                  <TableCell sx={bodyCellStyle}>
                    {/* Responsive pass (F.9): onClick moved off the inner <img>
                        onto the IconButton, so the tap target is the button's
                        44px box rather than the icon's own pixels, and delete
                        is spaced away from its neighbours. */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <IconButton
                        aria-label="Preview document"
                        sx={documentActionBtnSx}
                        onClick={() => handlePreview(row.document_id)}
                      >
                        <img src={eye} alt="" />
                      </IconButton>
                      <IconButton
                        aria-label="Delete document"
                        sx={documentActionBtnSx}
                        onClick={() => {
                          setSelectedDocId(row.document_id);
                          setShowConfirmDelete(true);
                        }}
                      >
                        <img src={trash} alt="" />
                      </IconButton>
                      <IconButton
                        aria-label="Download document"
                        sx={documentActionBtnSx}
                        onClick={() =>
                          handleDownload(row.document_id, row.file_name)
                        }
                      >
                        <img src={downloadIcon} alt="" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </TableContainer>
        </Box>
      ) : (
        <Typography sx={{ padding: 4 }}>No Documents to display</Typography>
      )}
      <ConfirmDeletePopup
        open={showConfirmDelete}
        onClose={handleCloseConfirmPopup}
        confirmDelete={handleConfirmDelete}
      />
      <VerifiedPopup
        label={popupLabel}
        open={showVerified}
        onClose={() => setShowVerified(false)}
      />

      <Dialog
        open={openPreview}
        onClose={handleClosePreview}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Document Preview</DialogTitle>
        <DialogContent dividers>
          {selectedDoc ? (
            <object
              data={selectedDoc.file_url}
              type="application/pdf"
              width="100%"
              height="600px"
            >
              <p>
                PDF preview is not supported in this browser.{" "}
                <a
                  href={selectedDoc.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download PDF
                </a>
              </p>
            </object>
          ) : (
            <Typography>Loading...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClosePreview}
            color="secondary"
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/* Responsive pass (F.9): a real 44px touch target for the row actions. */
const documentActionBtnSx = {
  width: 44,
  height: 44,
  borderRadius: "10px",
};

const headerCellStyle = {
  fontWeight: 600,
  fontSize: "14px",
  padding: "12px 12px",
  color: "#1f1f1f",
  borderBottom: "1px solid #d9d9d9",
};

const bodyCellStyle = {
  fontSize: "14px",
  //padding: "24px 32px",
  borderBottom: "1px solid #d9d9d9",
};
